import { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaTrash,
  FaCheckCircle,
  FaRupeeSign,
  FaUserTie,
  FaCalendarAlt,
  FaSpinner,
  FaTimes,
  FaChalkboardTeacher,
  FaRunning,
  FaClipboardList,
  FaExclamationTriangle,
  FaRedo,
  FaCreditCard,
  FaMoneyBillWave,
  FaClock,
  FaUserFriends,
  FaCopy,
  FaShare,
  FaWhatsapp,
  FaEnvelope
} from 'react-icons/fa';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import yogBg from '../../images/yg1.jpg';
import UserContext from '../../Components/UserContext';
import { formatPaymentStatus, getPaymentStatusColor } from '../../utils/paymentStatusFormatter';
import { formatDateLocale } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/currencyFormatter';
import ConfirmDialog from '../../Components/ConfirmDialog';

function ProgressBar({ progress }) {
  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className="bg-blue-600 h-2.5 rounded-full"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
}

function MyPlans() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('Credit/Debit Card');
  const processedLocationKeyRef = useRef(null);
  const [isPWA, setIsPWA] = useState(false);

  // Referral code states
  const [referralCodes, setReferralCodes] = useState([]);
  const [isLoadingReferralCodes, setIsLoadingReferralCodes] = useState(false);
  const [showReferralCodes, setShowReferralCodes] = useState(false);
  const [paymentReferralCode, setPaymentReferralCode] = useState('');
  const [referralCodeDiscount, setReferralCodeDiscount] = useState(null);
  const [isValidatingReferralCode, setIsValidatingReferralCode] = useState(false);

  // Fetch student plans from backend
  const fetchPlans = useCallback(async (isRetry = false) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await HttpInterceptor.get('/api/students/plans');
      if (response.status === 200) {
        // Handle both array and object responses
        const plansData = Array.isArray(response.data) ? response.data : (response.data?.data || []);

        // Deduplicate plans by id (or _id) to prevent duplicate displays
        const uniquePlans = plansData.reduce((acc, plan) => {
          const planId = plan.id || plan._id;
          if (planId && !acc.find(p => (p.id || p._id) === planId)) {
            acc.push(plan);
          }
          return acc;
        }, []);

        setPlans(uniquePlans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load plans';

      if (retryCount < MAX_RETRIES && !isRetry) {
        setError({
          message: errorMessage,
          retryable: true
        });
        toast.error('Failed to load plans. Please try again.');
      } else {
        setError({
          message: 'Unable to load plans. Please check your connection and try again later.',
          retryable: false
        });
        toast.error('Unable to load plans');
      }
      setPlans([]);
    } finally {
      setIsLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Fetch referral codes
  const fetchReferralCodes = useCallback(async () => {
    try {
      setIsLoadingReferralCodes(true);
      const userId = session?.userId || session?.id;
      if (!userId) return;

      const response = await HttpInterceptor.get(`/api/referral-codes/user/${userId}`);
      if (response.status === 200) {
        setReferralCodes(response.data?.referralCodes || []);
      }
    } catch (error) {
      console.error('Error fetching referral codes:', error);
    } finally {
      setIsLoadingReferralCodes(false);
    }
  }, [session]);

  useEffect(() => {
    if (showReferralCodes) {
      fetchReferralCodes();
    }
  }, [showReferralCodes, fetchReferralCodes]);

  // Copy referral code to clipboard
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Referral code copied to clipboard!');
  };

  // Generate shareable link
  const handleGenerateShareLink = async (code) => {
    try {
      const response = await HttpInterceptor.get(`/api/referral-codes/share-link/${code}`);
      if (response.status === 200) {
        const shareableLink = response.data?.shareableLink;
        navigator.clipboard.writeText(shareableLink);
        toast.success('Shareable link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    }
  };

  // Share via WhatsApp
  const handleShareWhatsApp = (code) => {
    const message = `Join me on YogVital! Use my referral code: ${code}`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Share via Email
  const handleShareEmail = (code) => {
    const subject = 'Join me on YogVital!';
    const body = `Hi! I'd love for you to join me on YogVital. Use my referral code: ${code}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      active: { color: 'bg-green-100 text-green-800', text: 'Active' },
      used: { color: 'bg-blue-100 text-blue-800', text: 'Used' },
      expired: { color: 'bg-red-100 text-red-800', text: 'Expired' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchPlans(true);
  };

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [planToPay, setPlanToPay] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'default'
  });

  // Update student status via API
  const updateStudentStatus = async (planId, updates) => {
    try {
      const response = await HttpInterceptor.patch(`/api/students/status/${planId}`, updates);
      if (response.status === 200) {
        toast.success('Student status updated successfully');
        return response.data;
      }
    } catch (error) {
      console.error('Error updating student status:', error);
      toast.error(error.response?.data?.message || 'Failed to update student status');
      throw error;
    }
  };

  useEffect(() => {
    // Only process if we haven't already processed this location state
    if (location.state?.newPlans && processedLocationKeyRef.current !== location.key) {
      processedLocationKeyRef.current = location.key;
      // Refetch plans from API instead of manually adding to avoid duplicates
      fetchPlans().then(() => {
        // After fetching, check if we have a message and update it with actual count
        if (location.state.message) {
          // Use the actual number of new plans if available, otherwise use the message as-is
          const newPlansCount = location.state.newPlans?.length || 0;
          if (newPlansCount > 0) {
            const updatedMessage = `Your ${newPlansCount} program enrollment ${newPlansCount > 1 ? 'requests have' : 'request has'} been submitted!`;
            setSuccessMessage(updatedMessage);
          } else {
            setSuccessMessage(location.state.message);
          }
          const timer = setTimeout(() => setSuccessMessage(''), 5000);
          return () => clearTimeout(timer);
        }
      });
      // Clear location.state to prevent re-processing
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.key, navigate, location.pathname, fetchPlans]);

  const calculatePlanProgress = (plan) => {
    const total = Number(plan.totalClasses) || 0;
    const completed = Number(plan.completedClasses) || 0;
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const handleDelete = (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this plan? This action cannot be undone.',
      onConfirm: () => confirmDelete(id),
      variant: 'danger'
    });
  };

  const confirmDelete = async (planId) => {
    try {
      const response = await HttpInterceptor.delete(`/api/students/enrollment/${planId}`);

      if (response.status === 200) {
        // Remove from local state
        setPlans(plans.filter(p => (p.id || p._id) !== planId));
        toast.success('Enrollment deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting enrollment:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete enrollment';
      toast.error(errorMessage);
    }
  };

  const handleRequestPayment = (plan) => {
    if (plan.status === 'active') {
      setPlanToPay(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentComplete = async () => {
    if (!planToPay) {
      toast.error('Payment plan information is missing');
      return;
    }

    setIsProcessingPayment(true);

    try {
      // Get required data from plan
      // The plan.id is the StudentModel _id (student enrollment record)
      const studentEnrollmentId = planToPay.id || planToPay._id;
      const trainerId = planToPay.trainerId;
      const amount = planToPay.price || planToPay.totalAmount || 0;
      const studentName = session?.name || planToPay.studentName || 'Student';

      if (!studentEnrollmentId || !trainerId || !amount) {
        throw new Error('Missing required payment information');
      }

      // Fetch student details to get programId (required for payment processing)
      let programId = planToPay.programId || planToPay.program?.id || planToPay.programId;

      if (!programId) {
        // Try to get from student details endpoint
        const studentResponse = await HttpInterceptor.get(`/api/students/${studentEnrollmentId}`);
        if (studentResponse.status === 200) {
          const studentData = studentResponse.data?.data || studentResponse.data;

          // Try multiple paths to extract programId
          const fetchedProgramId =
            studentData?.enrollments?.[0]?.program?.id || // From enrollments array
            studentData?.program?.programId?._id || // From program.programId._id
            studentData?.program?.programId || // From program.programId (string)
            studentData?.enrollments?.[0]?.programId; // From enrollments[0].programId

          if (fetchedProgramId) {
            programId = typeof fetchedProgramId === 'object' ? (fetchedProgramId._id || fetchedProgramId.toString()) : fetchedProgramId.toString();
          }
        }
      }

      if (!programId) {
        console.error('Payment form data:', { planToPay, studentEnrollmentId, trainerId });
        throw new Error('Program information not found. Please try again.');
      }

      // Process payment through API
      const paymentResponse = await HttpInterceptor.post('/api/fee-plan/transactions/process', {
        studentId: studentEnrollmentId,
        programId,
        trainerId,
        amount: referralCodeDiscount ? referralCodeDiscount.finalAmount : amount,
        studentName,
        paymentMethod: selectedPaymentMethod,
        paymentMethodId: null, // Can be added if payment gateway integration is needed
        referralCode: paymentReferralCode || undefined
      });

      if (paymentResponse.status === 201) {
        // Payment processed successfully, now update student status
        const updatedPlan = {
          ...planToPay,
          paymentStatus: 'paid',
          status: 'active',
          startDate: new Date().toISOString().split('T')[0]
        };

        // Update student status
        await updateStudentStatus(studentEnrollmentId, {
          status: 'active',
          joinDate: updatedPlan.startDate,
          paymentStatus: 'paid'
        });

        toast.success('Payment processed successfully!');

        // Refresh plans to get updated data
        await fetchPlans();

        setShowPaymentModal(false);
        setPlanToPay(null);
        setPaymentReferralCode('');
        setReferralCodeDiscount(null);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to process payment';
      toast.error(errorMessage);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleAccessClasses = (plan) => {
    if (plan.paymentStatus === 'paid') {
      navigate('/student/myclasses', { state: { planId: plan.id || plan._id } });
    }
  };

  // Handle disconnect from trainer
  const handleDisconnect = async (planId) => {
    try {
      const response = await HttpInterceptor.patch(`/api/students/${planId}/disconnect`);
      if (response.status === 200) {
        toast.success('Successfully disconnected from trainer');
        // Refresh plans list
        fetchPlans();
      }
    } catch (error) {
      console.error('Error disconnecting from trainer:', error);
      const errorMessage = error.response?.data?.message || 'Failed to disconnect from trainer';
      toast.error(errorMessage);
    }
  };

  // Fetch payment reminders
  const [paymentReminders, setPaymentReminders] = useState([]);
  const [showRemindersModal, setShowRemindersModal] = useState(false);

  useEffect(() => {
    const fetchPaymentReminders = async () => {
      try {
        const response = await HttpInterceptor.get('/api/students/payment-reminders');
        if (response.status === 200) {
          const remindersData = response.data?.data || [];
          setPaymentReminders(remindersData);
        }
      } catch (error) {
        console.error('Error fetching payment reminders:', error);
      }
    };

    fetchPaymentReminders();
  }, []);

  return (
    <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-gray-50'}`}>
      {/* Hero Section */}
      <div className={`relative ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : 'bg-gradient-to-r from-blue-600 to-purple-700'} ${isPWA ? 'text-gray-900' : 'text-white'}`}>
        {isPWA && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
        )}
        {!isPWA && (
          <div className="absolute inset-0 bg-black opacity-20"></div>
        )}
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-teal-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'}`}>
                My Training Plans
              </h1>
              <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-xl opacity-90'}`}>
                Track your progress and manage your enrolled programs
              </p>
            </div>
            {!isPWA && (
              <div className="hidden md:block">
                <img src={yogBg} alt="Yoga" className="w-32 h-32 rounded-full object-cover" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <section className={`${isPWA ? 'py-6 sm:py-8 pb-10 sm:pb-12' : 'py-8'} ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-white'} relative`}>
        {isPWA && (
          <>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-200/40 to-transparent"></div>
          </>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid ${isPWA ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-6'} ${isPWA ? 'mb-6' : 'mb-8'}`}>
            {/* Total Plans */}
            <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/20 border-2 border-teal-200/50 shadow-xl hover:shadow-2xl relative overflow-hidden transition-all duration-300' : 'bg-blue-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5' : 'p-6'} ${isPWA ? 'rounded-2xl' : ''}`}>
              {isPWA && (
                <>
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-300/60 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-2xl"></div>
                </>
              )}
              <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                {isPWA ? (
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Total Plans</p>
                      <p className="text-2xl sm:text-3xl font-bold text-teal-800 leading-tight drop-shadow-sm">{plans.length}</p>
                    </div>
                    <div className="bg-gradient-to-br from-teal-50 to-blue-50 text-teal-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-teal-100/50 flex-shrink-0 ml-2">
                      <FaClipboardList className="text-lg sm:text-xl" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`text-2xl font-bold text-blue-600 mb-2`}>{plans.length}</div>
                    <div className="text-sm text-blue-600">Total Plans</div>
                  </>
                )}
              </div>
            </div>

            {/* Active Plans */}
            <div className={`${isPWA ? 'bg-gradient-to-br from-white to-emerald-50/20 border-2 border-emerald-200/50 shadow-xl hover:shadow-2xl relative overflow-hidden transition-all duration-300' : 'bg-green-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5' : 'p-6'} ${isPWA ? 'rounded-2xl' : ''}`}>
              {isPWA && (
                <>
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-300/60 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-100/20 to-green-100/10 rounded-full blur-2xl"></div>
                </>
              )}
              <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                {isPWA ? (
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Active Plans</p>
                      <p className="text-2xl sm:text-3xl font-bold text-emerald-800 leading-tight drop-shadow-sm">
                        {useMemo(() => plans.filter(p => p.status === 'active').length, [plans])}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 text-emerald-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-emerald-100/50 flex-shrink-0 ml-2">
                      <FaCheckCircle className="text-lg sm:text-xl" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`text-2xl font-bold text-green-600 mb-2`}>
                      {useMemo(() => plans.filter(p => p.status === 'active').length, [plans])}
                    </div>
                    <div className="text-sm text-green-600">Active Plans</div>
                  </>
                )}
              </div>
            </div>

            {/* Pending Payments */}
            <div className={`${isPWA ? 'bg-gradient-to-br from-white to-blue-50/20 border-2 border-blue-200/50 shadow-xl hover:shadow-2xl relative overflow-hidden transition-all duration-300' : 'bg-yellow-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5' : 'p-6'} ${isPWA ? 'rounded-2xl' : ''}`}>
              {isPWA && (
                <>
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-300/60 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100/20 to-indigo-100/10 rounded-full blur-2xl"></div>
                </>
              )}
              <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                {isPWA ? (
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Pending Payments</p>
                      <p className="text-2xl sm:text-3xl font-bold text-blue-800 leading-tight drop-shadow-sm">
                        {useMemo(() => plans.filter(p =>
                          p.status === 'active' &&
                          p.paymentStatus === 'pending'
                        ).length, [plans])}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-blue-100/50 flex-shrink-0 ml-2">
                      <FaMoneyBillWave className="text-lg sm:text-xl" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`text-2xl font-bold text-yellow-600 mb-2`}>
                      {useMemo(() => plans.filter(p =>
                        p.status === 'active' &&
                        p.paymentStatus === 'pending'
                      ).length, [plans])}
                    </div>
                    <div className="text-sm text-yellow-600">Pending Payments</div>
                  </>
                )}
              </div>
            </div>

            {/* Pending Approval */}
            <div className={`${isPWA ? 'bg-gradient-to-br from-white to-indigo-50/20 border-2 border-indigo-200/50 shadow-xl hover:shadow-2xl relative overflow-hidden transition-all duration-300' : 'bg-purple-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5' : 'p-6'} ${isPWA ? 'rounded-2xl' : ''}`}>
              {isPWA && (
                <>
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-indigo-300/60 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-100/20 to-purple-100/10 rounded-full blur-2xl"></div>
                </>
              )}
              <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                {isPWA ? (
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Pending Approval</p>
                      <p className="text-2xl sm:text-3xl font-bold text-indigo-800 leading-tight drop-shadow-sm">
                        {useMemo(() => plans.filter(p => p.status === 'pending_approval' || p.status === 'pending').length, [plans])}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 text-indigo-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-indigo-100/50 flex-shrink-0 ml-2">
                      <FaClock className="text-lg sm:text-xl" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`text-2xl font-bold text-purple-600 mb-2`}>
                      {useMemo(() => plans.filter(p => p.status === 'pending_approval' || p.status === 'pending').length, [plans])}
                    </div>
                    <div className="text-sm text-purple-600">Pending Approval</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-6 sm:py-8' : 'py-8'}`}>
        <div className={`flex flex-col md:flex-row justify-between items-start md:items-center ${isPWA ? 'mb-6' : 'mb-8'}`}>
          <div>
            <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-teal-900' : 'text-gray-800'} ${isPWA ? 'mb-1.5' : 'mb-2'} ${isPWA ? 'drop-shadow-sm' : ''}`}>
              Your Plans Overview
            </h2>
            <p className={`${isPWA ? 'text-xs sm:text-sm' : ''} ${isPWA ? 'text-teal-800/90' : 'text-gray-600'}`}>
              Manage and track your enrolled training programs
            </p>
          </div>
          <div className={`flex ${isPWA ? 'flex-col sm:flex-row' : ''} ${isPWA ? 'gap-2' : 'gap-4'} mt-4 md:mt-0`}>
            <button
              onClick={() => setShowReferralCodes(!showReferralCodes)}
              className={`${isPWA ? 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 shadow-lg border border-purple-400/50' : 'bg-purple-600 hover:bg-purple-700'} text-white ${isPWA ? 'px-4 py-2 text-xs sm:text-sm' : 'px-6 py-3'} rounded-lg transition-colors flex items-center relative`}
            >
              <FaUserFriends className={isPWA ? 'mr-1.5 text-sm' : 'mr-2'} />
              <span className={isPWA ? 'truncate' : ''}>My Referral Codes</span>
            </button>
            {paymentReminders.length > 0 && (
              <button
                onClick={() => setShowRemindersModal(true)}
                className={`${isPWA ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 shadow-lg border border-amber-400/50' : 'bg-yellow-600 hover:bg-yellow-700'} text-white ${isPWA ? 'px-4 py-2 text-xs sm:text-sm' : 'px-6 py-3'} rounded-lg transition-colors flex items-center relative`}
              >
                <FaExclamationTriangle className={isPWA ? 'mr-1.5 text-sm' : 'mr-2'} />
                <span className={isPWA ? 'truncate' : ''}>Payment Reminders</span>
                <span className={`ml-2 bg-red-500 text-white ${isPWA ? 'text-xs' : 'text-xs'} rounded-full ${isPWA ? 'px-1.5 py-0.5' : 'px-2 py-1'}`}>
                  {paymentReminders.length}
                </span>
              </button>
            )}
          </div>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg border border-green-200">
            {successMessage}
          </div>
        )}

        {/* Referral Codes Section */}
        {showReferralCodes && (
          <div className={`mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-purple-50/20 border-2 border-purple-200/50 shadow-xl' : 'bg-white border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-6 rounded-lg'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} font-bold ${isPWA ? 'text-purple-800' : 'text-gray-900'} flex items-center gap-2`}>
                <FaUserFriends className={isPWA ? 'text-purple-600' : 'text-purple-600'} />
                My Referral Codes
              </h3>
              <button
                onClick={() => setShowReferralCodes(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            {isLoadingReferralCodes ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading referral codes...</p>
              </div>
            ) : referralCodes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {referralCodes.map((code) => (
                  <div key={code._id} className={`${isPWA ? 'bg-white/80 border-2 border-purple-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-4 sm:p-5 rounded-xl' : 'p-5 rounded-lg'} relative overflow-hidden group`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <code className="text-lg font-bold text-purple-600">{code.code}</code>
                          {getStatusBadge(code.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          Discount: <span className="font-semibold text-purple-600">{code.discountPercentage}%</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Expires: {formatDateLocale(code.expiresAt)}
                        </p>
                        {code.referredStudentId && (
                          <p className="text-xs text-green-600 mt-2">
                            Referred: {code.referredStudentId?.personalDetails?.name || 'Student'} enrolled
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handleCopyCode(code.code)}
                        className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <FaCopy /> Copy Code
                      </button>
                      <button
                        onClick={() => handleGenerateShareLink(code.code)}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                        title="Copy Shareable Link"
                      >
                        <FaShare />
                      </button>
                      <button
                        onClick={() => handleShareWhatsApp(code.code)}
                        className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                        title="Share via WhatsApp"
                      >
                        <FaWhatsapp />
                      </button>
                      <button
                        onClick={() => handleShareEmail(code.code)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                        title="Share via Email"
                      >
                        <FaEnvelope />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaUserFriends className="text-4xl text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No referral codes yet</p>
                <p className="text-sm text-gray-500 mt-2">You'll receive a referral code after making your first payment</p>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
            <FaSpinner className={`${isPWA ? 'text-4xl sm:text-5xl' : 'text-6xl'} mx-auto ${isPWA ? 'text-teal-600' : 'text-blue-600'} animate-spin ${isPWA ? 'mb-3' : 'mb-4'}`} />
            <p className={`${isPWA ? 'text-sm sm:text-base' : ''} ${isPWA ? 'text-teal-800' : 'text-gray-600'}`}>
              Loading your plans...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
            <div className={`${isPWA ? 'text-red-500' : 'text-red-400'} ${isPWA ? 'mb-3' : 'mb-4'}`}>
              <FaExclamationTriangle className={`${isPWA ? 'text-4xl sm:text-5xl' : 'text-6xl'} mx-auto`} />
            </div>
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-medium ${isPWA ? 'text-teal-900' : 'text-gray-900'} ${isPWA ? 'mb-1.5' : 'mb-2'}`}>
              Unable to Load Plans
            </h3>
            <p className={`${isPWA ? 'text-xs sm:text-sm' : ''} ${isPWA ? 'text-teal-700/90' : 'text-gray-500'} ${isPWA ? 'mb-5' : 'mb-6'}`}>
              {error.message}
            </p>
            {error.retryable && (
              <div className={`flex ${isPWA ? 'flex-col sm:flex-row' : ''} justify-center ${isPWA ? 'gap-2' : 'gap-4'}`}>
                <button
                  onClick={handleRetry}
                  disabled={isLoading}
                  className={`${isPWA ? 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-lg border border-teal-500/50' : 'bg-blue-600 hover:bg-blue-700'} text-white ${isPWA ? 'px-4 py-2 text-xs sm:text-sm' : 'px-6 py-3'} rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed justify-center`}
                >
                  <FaRedo className={isLoading ? 'animate-spin' : ''} />
                  Retry
                </button>
                <button
                  onClick={() => navigate('/student/services')}
                  className={`${isPWA ? 'bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 border border-gray-400/50' : 'bg-gray-200 hover:bg-gray-300'} ${isPWA ? 'text-gray-800' : 'text-gray-700'} ${isPWA ? 'px-4 py-2 text-xs sm:text-sm' : 'px-6 py-3'} rounded-lg transition-colors`}
                >
                  Browse Programs
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!error && !isLoading && plans.length === 0 && (
          <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
            <div className={`${isPWA ? 'text-teal-400/70' : 'text-gray-400'} ${isPWA ? 'mb-3' : 'mb-4'}`}>
              <FaClipboardList className={`${isPWA ? 'text-4xl sm:text-5xl' : 'text-6xl'} mx-auto`} />
            </div>
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-medium ${isPWA ? 'text-teal-900' : 'text-gray-900'} ${isPWA ? 'mb-1.5' : 'mb-2'}`}>
              No Plans Yet
            </h3>
            <p className={`${isPWA ? 'text-xs sm:text-sm' : ''} ${isPWA ? 'text-teal-700/90' : 'text-gray-500'} ${isPWA ? 'mb-5' : 'mb-6'}`}>
              Start your fitness journey by enrolling in a training plan
            </p>
            <button
              onClick={() => navigate('/student/services')}
              className={`${isPWA ? 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-lg border border-teal-500/50' : 'bg-blue-600 hover:bg-blue-700'} text-white ${isPWA ? 'px-4 py-2 text-xs sm:text-sm' : 'px-6 py-3'} rounded-lg transition-colors`}
            >
              Browse Available Plans
            </button>
          </div>
        )}

        {/* Plans Grid - Only show when not loading and no error */}
        {!error && !isLoading && plans.length > 0 && (
          <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 ${isPWA ? 'gap-4' : 'gap-6'}`}>
            {plans.map((plan, index) => (
              <div key={plan.id || plan._id || `plan-${index}`} className={`${isPWA ? 'bg-gradient-to-br from-white via-teal-50/30 to-blue-50/20 border-2 border-teal-200/60 shadow-lg' : 'bg-white shadow-sm border border-gray-200'} rounded-xl ${isPWA ? 'hover:shadow-xl hover:border-teal-300' : 'hover:shadow-md'} transition-all duration-200`}>
                <div className={`${isPWA ? 'p-4 sm:p-5' : 'p-6'}`}>
                  {/* Plan Header */}
                  <div className={`flex items-start justify-between ${isPWA ? 'mb-3' : 'mb-4'}`}>
                    <div className="flex-1">
                      <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-semibold ${isPWA ? 'text-teal-900' : 'text-gray-800'} ${isPWA ? 'mb-1' : 'mb-1'}`}>
                        {plan.name}
                      </h3>
                      <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-teal-700/90' : 'text-gray-600'} ${isPWA ? 'mb-1.5' : 'mb-2'}`}>
                        {plan.program}
                      </p>
                      <div className={`flex items-center ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-teal-700/80' : 'text-gray-500'}`}>
                        <FaUserTie className={isPWA ? 'mr-1.5 text-sm' : 'mr-2'} />
                        {plan.trainer}
                      </div>
                    </div>
                    <div className="text-right">
                      {plan.originalPrice && plan.originalPrice > plan.price ? (
                        <>
                          <div className="text-lg font-bold text-gray-800">
                            <span className="line-through text-gray-400 text-sm mr-2">{formatCurrency(plan.originalPrice)}</span>
                            <span>{formatCurrency(plan.price)}</span>
                          </div>
                          {plan.discountPercentage && (
                            <div className="text-xs text-green-600 font-medium">
                              {plan.discountPercentage.toFixed(0)}% off
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-lg font-bold text-gray-800">{formatCurrency(plan.price)}</div>
                      )}
                      <div className="text-sm text-gray-500">Total</div>
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {plan.status === 'pending' || plan.status === 'pending_approval' || plan.status === 'on_hold'
                          ? 'Application Date:'
                          : 'Enrollment Activated:'}
                      </span>
                      <span className="font-medium" title={plan.status === 'active' ? 'Date when your enrollment was activated by the trainer' : 'Date when you applied for enrollment'}>
                        {plan.status === 'pending' || plan.status === 'pending_approval' || plan.status === 'on_hold'
                          ? (plan.enrollmentDate ? formatDateLocale(plan.enrollmentDate) : 'N/A')
                          : (plan.startDate ? formatDateLocale(plan.startDate) : 'Not started')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status:</span>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${plan.status === 'active' ? 'bg-green-100 text-green-800' :
                          plan.status === 'pending' || plan.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                            plan.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              plan.status === 'on_hold' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                        }`}>
                        {plan.status === 'pending' ? 'pending approval' : plan.status.replace('_', ' ')}
                      </span>
                    </div>
                    {/* Only show payment status when enrollment is active */}
                    {plan.status === 'active' && (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Payment:</span>
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentStatusColor(plan.paymentStatus)}`}>
                            {formatPaymentStatus(plan.paymentStatus)}
                          </span>
                        </div>
                        {plan.price > 0 && (
                          <>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">Total Amount:</span>
                              <span className="font-medium">{formatCurrency(plan.price)}</span>
                            </div>
                            {plan.paidAmount > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Paid:</span>
                                <span className="font-medium text-green-600">{formatCurrency(plan.paidAmount)}</span>
                              </div>
                            )}
                            {plan.dueAmount > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Pending:</span>
                                <span className="font-medium text-orange-600">{formatCurrency(plan.dueAmount)}</span>
                              </div>
                            )}
                          </>
                        )}
                        {/* Installment Plan Information */}
                        {plan.installmentPlan && plan.installmentPlan.intervals && plan.installmentPlan.intervals.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="text-gray-500 flex items-center gap-1">
                                <FaCreditCard className="text-xs" />
                                Payment Plan:
                              </span>
                              <span className="font-medium text-blue-600">{plan.installmentPlan.planName}</span>
                            </div>
                            <div className="text-xs text-gray-600 mb-2">
                              {plan.installmentPlan.numberOfInstallments} installments
                            </div>
                            <div className="max-h-32 overflow-y-auto space-y-1">
                              {plan.installmentPlan.intervals.slice(0, 3).map((interval) => (
                                <div key={interval.installmentNumber} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                                  <span className="text-gray-600">
                                    Installment {interval.installmentNumber}:
                                  </span>
                                  <div className="text-right">
                                    <span className="font-medium">{formatCurrency(interval.amount)}</span>
                                    <span className="text-gray-500 ml-1">
                                      ({formatDateLocale(new Date(interval.dueDate), { month: 'short', day: 'numeric' })})
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {plan.installmentPlan.intervals.length > 3 && (
                                <div className="text-xs text-gray-500 text-center pt-1">
                                  +{plan.installmentPlan.intervals.length - 3} more installments
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Progress Bar - Only show if there are classes scheduled (totalClasses > 0) */}
                  {plan.status === 'active' && (() => {
                    const totalClasses = Number(plan.totalClasses) || 0;
                    return totalClasses > 0;
                  })() && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-600">Progress</span>
                          <span className="text-gray-800 font-medium">{(Number(plan.completedClasses) || 0)}/{(Number(plan.totalClasses) || 0)} classes</span>
                        </div>
                        <ProgressBar progress={calculatePlanProgress(plan)} />
                      </div>
                    )}

                  {/* Rejection Reason */}
                  {plan.status === 'rejected' && plan.rejectionReason && (
                    <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="text-sm text-red-800">
                        <strong>Rejection Reason:</strong> {plan.rejectionReason}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className={`flex flex-wrap ${isPWA ? 'gap-1.5' : 'gap-2'}`}>
                    {(plan.status === 'pending' || plan.status === 'pending_approval') && (
                      <div className={`w-full ${isPWA ? 'p-2.5' : 'p-3'} ${isPWA ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300/60' : 'bg-yellow-50 rounded-lg border border-yellow-200'} rounded-lg`}>
                        <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-amber-900 font-medium' : 'text-yellow-800'}`}>
                          <FaExclamationTriangle className={`inline ${isPWA ? 'mr-1 text-xs' : 'mr-1'}`} />
                          Waiting for trainer approval
                        </p>
                      </div>
                    )}

                    {plan.status === 'active' && plan.paymentStatus === 'pending' && (
                      <button
                        onClick={() => handleRequestPayment(plan)}
                        className={`w-full ${isPWA ? 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-md border border-teal-500/50' : 'bg-blue-600 hover:bg-blue-700'} text-white ${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-3 py-2 rounded-lg text-sm'} rounded-lg transition-all flex items-center justify-center`}
                      >
                        <FaRupeeSign className={isPWA ? 'mr-1 text-xs' : 'mr-1'} />
                        Request Payment
                      </button>
                    )}

                    {plan.paymentStatus === 'paid' && (
                      <button
                        onClick={() => handleAccessClasses(plan)}
                        className={`w-full ${isPWA ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md border border-indigo-500/50' : 'bg-purple-600 hover:bg-purple-700'} text-white ${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-3 py-2 rounded-lg text-sm'} rounded-lg transition-all flex items-center justify-center`}
                      >
                        <FaChalkboardTeacher className={isPWA ? 'mr-1 text-xs' : 'mr-1'} />
                        Access Classes
                      </button>
                    )}

                    {plan.status === 'active' && (
                      <button
                        onClick={() => {
                          setConfirmConfig({
                            isOpen: true,
                            title: 'Disconnect from Trainer',
                            message: 'Are you sure you want to disconnect from this trainer? This action cannot be undone.',
                            onConfirm: () => handleDisconnect(plan.id || plan._id),
                            variant: 'danger'
                          });
                        }}
                        className={`w-full ${isPWA ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 shadow-md border border-red-500/50' : 'bg-red-600 hover:bg-red-700'} text-white ${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-3 py-2 rounded-lg text-sm'} rounded-lg transition-all flex items-center justify-center`}
                      >
                        <FaTimes className={isPWA ? 'mr-1 text-xs' : 'mr-1'} />
                        Disconnect from Trainer
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(plan.id || plan._id)}
                      className={`w-full ${isPWA ? 'bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 border border-gray-400/50' : 'bg-gray-100 hover:bg-gray-200'} ${isPWA ? 'text-gray-800' : 'text-gray-700'} ${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-3 py-2 rounded-lg text-sm'} rounded-lg transition-all flex items-center justify-center`}
                    >
                      <FaTrash className={isPWA ? 'mr-1 text-xs' : 'mr-1'} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && planToPay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-2xl font-bold mb-4">Complete Payment</h3>

            <div className="space-y-4 mb-6">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Program</p>
                <p className="font-medium">{planToPay.name}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Trainer</p>
                <p className="font-medium">{planToPay.trainer}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Amount</p>
                <div className="flex items-center">
                  <FaRupeeSign className="text-gray-500 mr-1" />
                  {referralCodeDiscount ? (
                    <div>
                      <span className="line-through text-gray-400 mr-2">{planToPay.price}</span>
                      <span className="font-medium text-green-600">{referralCodeDiscount.finalAmount.toFixed(2)}</span>
                      <span className="text-xs text-green-600 ml-2">({referralCodeDiscount.percentage}% off)</span>
                    </div>
                  ) : (
                    <span className="font-medium">{planToPay.price}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Referral Code Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referral Code (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={paymentReferralCode}
                  onChange={async (e) => {
                    const code = e.target.value.toUpperCase().trim();
                    setPaymentReferralCode(code);
                    setReferralCodeDiscount(null);

                    if (code.length > 0) {
                      setIsValidatingReferralCode(true);
                      try {
                        const response = await HttpInterceptor.get(`/api/referral-codes/validate/${code}`);
                        if (response.data?.valid) {
                          const discount = response.data.referralCode?.discountPercentage || 0;
                          const amount = planToPay.price || planToPay.totalAmount || 0;
                          const discountAmount = (amount * discount) / 100;
                          setReferralCodeDiscount({
                            percentage: discount,
                            amount: discountAmount,
                            finalAmount: amount - discountAmount
                          });
                          toast.success(`Referral code applied! ${discount}% discount`);
                        } else {
                          toast.error(response.data?.error || 'Invalid referral code');
                          setReferralCodeDiscount(null);
                        }
                      } catch (error) {
                        console.error('Error validating referral code:', error);
                        toast.error(error.response?.data?.error || 'Error validating referral code');
                        setReferralCodeDiscount(null);
                      } finally {
                        setIsValidatingReferralCode(false);
                      }
                    }
                  }}
                  placeholder="Enter referral code"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                {isValidatingReferralCode && (
                  <FaSpinner className="animate-spin text-purple-600 self-center" />
                )}
              </div>
              {referralCodeDiscount && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <span className="font-semibold">{referralCodeDiscount.percentage}% discount applied!</span>
                    <br />
                    <span className="text-xs">
                      Original: {formatCurrency(planToPay.price || planToPay.totalAmount || 0)} →
                      Final: {formatCurrency(referralCodeDiscount.finalAmount)}
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Method</h4>
              <div className="space-y-3">
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-500 cursor-pointer">
                  <input
                    type="radio"
                    name="payment"
                    value="Credit Card"
                    checked={selectedPaymentMethod === 'Credit Card'}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 block text-sm font-medium text-gray-700">Credit/Debit Card</span>
                </label>
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-500 cursor-pointer">
                  <input
                    type="radio"
                    name="payment"
                    value="UPI"
                    checked={selectedPaymentMethod === 'UPI'}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 block text-sm font-medium text-gray-700">UPI</span>
                </label>
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-500 cursor-pointer">
                  <input
                    type="radio"
                    name="payment"
                    value="Bank Transfer"
                    checked={selectedPaymentMethod === 'Bank Transfer'}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 block text-sm font-medium text-gray-700">Net Banking</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePaymentComplete}
                disabled={isProcessingPayment}
                className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center ${isProcessingPayment ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
              >
                {isProcessingPayment ? (
                  <>
                    <FaSpinner className="mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="mr-2" />
                    Confirm Payment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Reminders Modal */}
      {showRemindersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">Payment Reminders</h3>
              <button
                onClick={() => setShowRemindersModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            {paymentReminders.length === 0 ? (
              <div className="text-center py-12">
                <FaCheckCircle className="text-4xl text-green-500 mx-auto mb-4" />
                <p className="text-gray-600">No payment reminders at this time</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentReminders.map((reminder, index) => (
                  <div key={index} className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{reminder.program?.name || 'Program'}</h4>
                        <p className="text-sm text-gray-600">Trainer: {reminder.trainer?.name || 'Trainer'}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDateLocale(reminder.reminder.date)}
                      </span>
                    </div>
                    {reminder.reminder.message && (
                      <p className="text-sm text-gray-700 mb-2">{reminder.reminder.message}</p>
                    )}
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-yellow-200">
                      <div className="text-sm">
                        <span className="text-gray-600">Due Amount: </span>
                        <span className="font-semibold text-gray-900">
                          {reminder.payment.currency} {reminder.payment.dueAmount}
                        </span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${reminder.payment.status === 'pending'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                        }`}>
                        {reminder.payment.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        variant={confirmConfig.variant}
      />
    </div>
  );
}

export default MyPlans;